import React from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";

import localizations from "@src/localizations";
import { useStores } from "@stores";
import { captureError } from "@src/utils/analytics";
import Button from "@components/Button";

/**
 * Last line of defence against a blank page. Without it, any error thrown while
 * rendering unmounts the whole React tree and leaves an empty white screen.
 *
 * A common trigger we cannot fix in our code: browser page translation or an
 * extension rewriting the DOM, after which React throws
 * `NotFoundError: Failed to execute 'removeChild' on 'Node'` the next time a
 * list shrinks (e.g. deleting a field). The crash is reported to PostHog.
 */
interface State {
  error: Error | null;
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    captureError(error, {
      source: "react_error_boundary",
      componentStack: info.componentStack,
      path: window.location.pathname,
    });
  }

  render() {
    if (this.state.error) return <ErrorFallback />;
    return this.props.children;
  }
}

const Root = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px 16px;
  box-sizing: border-box;
  text-align: center;
  font-family: inherit;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #2b2d30;
`;

const Body = styled.p`
  margin: 0;
  max-width: 440px;
  font-size: 15px;
  line-height: 1.5;
  color: #6b7280;
`;

const ReloadButton = styled(Button)`
  width: auto;
  padding: 0 24px;
`;

const ErrorFallback: React.FC = observer(() => {
  const { settingsStore } = useStores();
  const lang = (localizations[settingsStore.language] ?? localizations.EN).errorBoundary;
  return (
    <Root>
      <Title>{lang.title}</Title>
      <Body>{lang.body}</Body>
      <ReloadButton kind="primary" onClick={() => window.location.reload()}>
        {lang.reload}
      </ReloadButton>
    </Root>
  );
});

export default ErrorBoundary;
